import { SPACES_SCHEMA_VERSION, LEGACY_SPACE_NAME, SPACE_ID_PATTERN } from './constants.js';
import { SpacesSchema, type Spaces } from './spaces.js';
import { loadBoard } from './migrate.js';

/**
 * Forward-only migration ladder for the Spaces envelope + the LEGACY single-board
 * migration. The Spaces analogue of migrate.ts, with one extra job: lifting every
 * existing production user's lone board into a Spaces envelope on first upgrade.
 *
 * Pipeline (mirrors loadBoard — ORDER MATTERS): read stamped version → run each
 * migration up to CURRENT → THEN Zod-validate. Migrate, then validate.
 *
 * PURE: no localStorage. The app reads BOTH raw strings (the spaces slot AND the
 * legacy board slot) and passes them in — the exact division loadBoard already uses.
 *
 * LEGACY MIGRATION (loss-proof, total, tested): when the spaces slot is ABSENT, a
 * VALID legacy board (run through loadBoard's own board ladder, so it is itself
 * migrated + validated) is wrapped as a single "Home" space. An invalid/absent/future
 * legacy board becomes a designed failure; the app falls back to a fresh default —
 * this function NEVER invents a board and NEVER throws.
 */

/** A single forward step, N -> N+1, over the opaque spaces blob (pre-validation). */
export type SpacesMigration = (raw: Record<string, unknown>) => Record<string, unknown>;

/**
 * Spaces-envelope migrations, indexed by SOURCE version. `SPACES_MIGRATIONS[n]`
 * migrates a version-`n` envelope to version-`n+1`. Empty today (v1 identity). To add
 * v2: write `migrate1to2`, set `SPACES_MIGRATIONS[1] = migrate1to2`, bump
 * `SPACES_SCHEMA_VERSION`. The loader picks it up automatically (same as the board ladder).
 */
export const SPACES_MIGRATIONS: Readonly<Record<number, SpacesMigration>> = {
  // 1: migrate1to2,
};

/** Where the loaded envelope came from — useful telemetry / first-run signalling. */
export type SpacesLoadSource =
  /** Loaded from the spaces slot (the normal path once migrated). */
  | 'spaces'
  /** Freshly lifted from the legacy single-board slot (first run after upgrade). */
  | 'legacy';

/** Result of a spaces-load attempt — a discriminated union, never an exception. */
export type SpacesLoadResult =
  | { readonly ok: true; readonly spaces: Spaces; readonly source: SpacesLoadSource }
  | { readonly ok: false; readonly reason: SpacesLoadFailure; readonly detail?: string };

export type SpacesLoadFailure =
  /** Stored version > app CURRENT: a newer build wrote this. Do not mangle. */
  | 'future-version'
  /** Not spaces-shaped, forged/missing version, failed validation, or nothing to load. */
  | 'invalid';

/** Read the stamped version from an untrusted blob, if plausibly present. */
function readVersion(raw: unknown): number | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const v = (raw as Record<string, unknown>).schemaVersion;
  return typeof v === 'number' && Number.isInteger(v) && v >= 1 ? v : null;
}

/**
 * Run a migration ladder from `fromVersion` up to `toVersion`. Pure — fresh blob, never
 * mutates input. Exported so the MECHANISM is directly testable (a fake v1→v2 hop)
 * without touching the production `SPACES_MIGRATIONS`. A gap throws (→ `invalid`).
 */
export function runSpacesMigrations(
  raw: Record<string, unknown>,
  fromVersion: number,
  toVersion: number,
  migrations: Readonly<Record<number, SpacesMigration>>,
): Record<string, unknown> {
  let current: Record<string, unknown> = { ...raw };
  for (let v = fromVersion; v < toVersion; v += 1) {
    const step = migrations[v];
    if (!step) {
      throw new Error(`missing spaces migration for v${v}`);
    }
    current = step(current);
  }
  return current;
}

/** Load a present spaces blob: version-check → migrate → validate. Pure, never throws. */
function loadSpacesBlob(rawSpaces: unknown): SpacesLoadResult {
  const version = readVersion(rawSpaces);
  if (version === null) {
    return { ok: false, reason: 'invalid', detail: 'missing or malformed spaces schemaVersion' };
  }
  if (version > SPACES_SCHEMA_VERSION) {
    return {
      ok: false,
      reason: 'future-version',
      detail: `stored spaces v${version} > app v${SPACES_SCHEMA_VERSION}`,
    };
  }
  const migrated = runSpacesMigrations(
    rawSpaces as Record<string, unknown>,
    version,
    SPACES_SCHEMA_VERSION,
    SPACES_MIGRATIONS,
  );
  const parsed = SpacesSchema.safeParse(migrated);
  if (!parsed.success) {
    return { ok: false, reason: 'invalid', detail: parsed.error.message };
  }
  return { ok: true, spaces: parsed.data, source: 'spaces' };
}

/**
 * LEGACY path: wrap a lone board into a single-"Home"-space envelope. Delegates the
 * board itself to `loadBoard`, so the board is migrated + validated by its OWN ladder
 * (loss-proof: nothing about the board is rebuilt or dropped). Maps loadBoard's failure
 * reasons straight through (future-version / invalid).
 */
function loadLegacy(rawLegacyBoard: unknown, legacySpaceId: string): SpacesLoadResult {
  // Guard the caller-provided id up front so a malformed id fails as `invalid`
  // (deterministic) rather than surfacing as an opaque schema error later.
  if (!SPACE_ID_PATTERN.test(legacySpaceId)) {
    return { ok: false, reason: 'invalid', detail: `malformed legacySpaceId "${legacySpaceId}"` };
  }

  const boardResult = loadBoard(rawLegacyBoard);
  if (!boardResult.ok) {
    // future-version stays future-version; everything else is invalid. Preserve
    // loadBoard's detail (omit the key when undefined — exactOptionalPropertyTypes).
    const reason: SpacesLoadFailure =
      boardResult.reason === 'future-version' ? 'future-version' : 'invalid';
    const detail = boardResult.detail ?? (reason === 'invalid' ? 'legacy board invalid' : undefined);
    return detail === undefined
      ? { ok: false, reason }
      : { ok: false, reason, detail };
  }

  const envelope = {
    schemaVersion: SPACES_SCHEMA_VERSION,
    spaces: [{ id: legacySpaceId, name: LEGACY_SPACE_NAME, board: boardResult.board }],
    activeSpaceId: legacySpaceId,
  };
  const parsed = SpacesSchema.safeParse(envelope);
  if (!parsed.success) {
    return { ok: false, reason: 'invalid', detail: parsed.error.message };
  }
  return { ok: true, spaces: parsed.data, source: 'legacy' };
}

/**
 * Load the persisted Spaces state. The single entry point the app funnels through.
 *
 *   - `rawSpaces`:      parsed SPACES_STORAGE_KEY blob (or null/undefined if absent).
 *   - `rawLegacyBoard`: parsed BOARD_STORAGE_KEY blob (or null) — consulted ONLY when
 *                       the spaces slot is absent (first run after upgrade).
 *   - `legacySpaceId`:  an app-generated space id, used ONLY on the legacy path (id
 *                       generation is the app's job, never the schema's).
 *
 * Decision order (deterministic):
 *   1. spaces slot PRESENT (non-null) → load it through the ladder. A corrupt or
 *      future-version spaces slot SURFACES as such — it never silently falls back to
 *      legacy (a broken spaces slot must not be hidden by an old board).
 *   2. spaces slot ABSENT → attempt the legacy migration from rawLegacyBoard.
 *   3. both absent (or legacy invalid) → a designed failure; the app seeds a default.
 *
 * NEVER throws, NEVER mutates input.
 */
export function loadSpaces(
  rawSpaces: unknown,
  rawLegacyBoard: unknown,
  legacySpaceId: string,
): SpacesLoadResult {
  try {
    // A present spaces slot takes precedence and must be respected (incl. its failures).
    if (rawSpaces !== null && rawSpaces !== undefined) {
      return loadSpacesBlob(rawSpaces);
    }
    // No spaces slot → try to lift the legacy single board.
    if (rawLegacyBoard !== null && rawLegacyBoard !== undefined) {
      return loadLegacy(rawLegacyBoard, legacySpaceId);
    }
    // Nothing to load — the app seeds a fresh default board/space.
    return { ok: false, reason: 'invalid', detail: 'no spaces slot and no legacy board' };
  } catch (err) {
    const detail = err instanceof Error ? err.message : 'unknown spaces-load error';
    return { ok: false, reason: 'invalid', detail };
  }
}
